/**
 * Force-update helper around @capawesome/capacitor-app-update.
 *
 * Detects (via the App Store / Play Store) whether a newer version of the app
 * is available and drives the update action:
 *   - Android: in-app immediate update (Google Play full-screen flow), with a
 *     Play Store fallback.
 *   - iOS: opens the App Store listing.
 *
 * Fail-open by design: any error (offline, store unreachable, app not yet
 * published) resolves to "no update" so users are never hard-locked.
 */
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import {
  AppUpdate,
  AppUpdateAvailability,
} from "@capawesome/capacitor-app-update";

// Numeric App Store ID for the iOS app (App Store Connect → App Information →
// "Apple ID"). Bundle id: it.dietasocial.web. Used to open the correct store
// listing so the user can actually complete the update.
export const IOS_APP_STORE_ID = "6746050415";

// Two-letter store country used for the iOS iTunes lookup and App Store URL.
const STORE_COUNTRY = "it";

// Canonical App Store listing URL — used as a browser fallback if the native
// store-open call is unavailable.
export const IOS_APP_STORE_URL = `https://apps.apple.com/${STORE_COUNTRY}/app/id${IOS_APP_STORE_ID}`;

/**
 * A usable iOS App Store ID is digits-only and non-zero. The old "000000000"
 * placeholder parses to 0, so this also rejects a mis-configured build — in
 * which case we deliberately never raise the (non-dismissable) update gate,
 * since there would be no way for the user to complete the update.
 */
const hasValidIosAppStoreId = () =>
  /^[0-9]+$/.test(IOS_APP_STORE_ID) && Number(IOS_APP_STORE_ID) > 0;

/**
 * Compare two dotted numeric version strings ("1.2.0").
 * Returns true when `a` is strictly greater than `b`.
 */
const isVersionGreater = (a, b) => {
  if (!a || !b) return false;
  const pa = String(a)
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  const pb = String(b)
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da > db) return true;
    if (da < db) return false;
  }
  return false;
};

const NO_UPDATE = { updateAvailable: false };

/**
 * Query the store for update availability.
 * @returns {Promise<{updateAvailable: boolean, immediateUpdateAllowed?: boolean,
 *   flexibleUpdateAllowed?: boolean, platform?: string,
 *   currentVersion?: string, availableVersion?: string}>}
 */
export async function getUpdateStatus() {
  const platform = Capacitor.getPlatform();
  if (platform === "web") return NO_UPDATE;

  // Fail-safe: on iOS the only way to satisfy the update is to open a valid App
  // Store listing. If the numeric ID isn't configured, never report an update —
  // otherwise the non-dismissable gate would trap the user with no way out.
  if (platform === "ios" && !hasValidIosAppStoreId()) return NO_UPDATE;

  try {
    const info = await AppUpdate.getAppUpdateInfo();

    const flaggedAvailable =
      info?.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;
    const newerVersion = isVersionGreater(
      info?.availableVersionName,
      info?.currentVersionName,
    );

    return {
      updateAvailable: Boolean(flaggedAvailable || newerVersion),
      immediateUpdateAllowed: Boolean(info?.immediateUpdateAllowed),
      flexibleUpdateAllowed: Boolean(info?.flexibleUpdateAllowed),
      platform,
      currentVersion: info?.currentVersionName,
      availableVersion: info?.availableVersionName,
    };
  } catch (_e) {
    // Fail-open — never block users on a transient/unsupported error.
    return NO_UPDATE;
  }
}

/**
 * Perform the update for the current platform.
 *   - Android: immediate in-app update (fallback to Play Store).
 *   - iOS: open the App Store listing.
 */
export async function performUpdate(status = {}) {
  const platform = status.platform || Capacitor.getPlatform();

  if (platform === "android") {
    if (status.immediateUpdateAllowed) {
      try {
        await AppUpdate.performImmediateUpdate();
        return;
      } catch (_e) {
        // User cancelled or the flow failed — fall back to the store page.
      }
    }
    await openStore();
    return;
  }

  // iOS (and any other native target) → open the store listing.
  await openStore();
}

async function openStore() {
  const platform = Capacitor.getPlatform();

  // Android resolves its own Play listing from the package name; iOS needs the
  // numeric App Store ID.
  try {
    await AppUpdate.openAppStore(
      platform === "ios" ? { appId: IOS_APP_STORE_ID } : {},
    );
    return;
  } catch (_e) {
    // Native store-open failed — fall through to a direct browser open.
  }

  // Fallback: open the listing in the system browser. Only meaningful on iOS,
  // where we have a canonical URL to hand.
  if (platform === "ios") {
    try {
      await Browser.open({ url: IOS_APP_STORE_URL });
    } catch (_e) {
      // Nothing else we can do; the gate re-checks on the next resume.
    }
  }
}
